<template>
  <ion-modal :is-open="isOpen" @didDismiss="$emit('close')" class="diagnostic-modal">
    <ion-header>
      <ion-toolbar>
        <ion-title>评论功能诊断</ion-title>
        <ion-buttons slot="end">
          <ion-button @click="$emit('close')">
            <ion-icon :icon="closeOutline"></ion-icon>
          </ion-button>
        </ion-buttons>
      </ion-toolbar>
    </ion-header>

    <ion-content class="ion-padding">
      <!-- 诊断结果 -->
      <div class="diagnostic-container">
        <div class="test-section">
          <h3>连接状态检查</h3>
          <div class="test-item">
            <div class="test-label">网络连接</div>
            <div class="test-status" :class="networkStatus">
              <ion-icon :icon="networkStatus === 'online' ? checkmarkCircleOutline : alertCircleOutline"></ion-icon>
              {{ networkStatus === 'online' ? '正常' : '异常' }}
            </div>
          </div>
          
          <div class="test-item">
            <div class="test-label">Gun.js节点连接</div>
            <div class="test-status" :class="gunConnectionStatus">
              <ion-icon :icon="gunConnectionStatus === 'connected' ? checkmarkCircleOutline : alertCircleOutline"></ion-icon>
              {{ gunConnectionStatus === 'connected' ? '已连接' : '未连接' }}
            </div>
          </div>
          
          <div class="test-item">
            <div class="test-label">评论保存测试</div>
            <div class="test-status" :class="commentTestStatus">
              <ion-icon v-if="commentTestStatus === 'testing'" :icon="hourglassOutline"></ion-icon>
              <ion-icon v-else-if="commentTestStatus === 'success'" :icon="checkmarkCircleOutline"></ion-icon>
              <ion-icon v-else :icon="alertCircleOutline"></ion-icon>
              {{ getTestStatusText(commentTestStatus) }}
            </div>
            <ion-button 
              v-if="commentTestStatus !== 'testing'" 
              fill="clear" 
              size="small" 
              @click="testCommentSave"
            >
              重新测试
            </ion-button>
          </div>
        </div>

        <!-- 修复建议 -->
        <div v-if="showFixSuggestions" class="fix-section">
          <h3>修复建议</h3>
          <div class="fix-list">
            <div v-for="suggestion in fixSuggestions" :key="suggestion.id" class="fix-item">
              <div class="fix-description">{{ suggestion.description }}</div>
              <ion-button 
                v-if="suggestion.action" 
                fill="outline" 
                size="small" 
                @click="suggestion.action"
                :disabled="suggestion.isExecuting"
              >
                {{ suggestion.isExecuting ? '执行中...' : suggestion.actionText }}
              </ion-button>
            </div>
          </div>
        </div>

        <!-- 详细信息 -->
        <div class="details-section">
          <h3>详细信息</h3>
          <div class="info-item">
            <strong>启用节点数量:</strong> {{ enabledPeersCount }}
          </div>
          <div class="info-item">
            <strong>队列请求数量:</strong> {{ queueSize }}
          </div>
          <div class="info-item">
            <strong>最后检查时间:</strong> {{ lastCheckTime }}
          </div>
        </div>

        <!-- 操作按钮 -->
        <div class="action-buttons">
          <ion-button expand="block" @click="runFullDiagnostic" :disabled="isRunningDiagnostic">
            {{ isRunningDiagnostic ? '诊断中...' : '重新诊断' }}
          </ion-button>
          
          <ion-button 
            expand="block" 
            color="warning" 
            @click="resetGunConnection"
            :disabled="isResettingConnection"
          >
            {{ isResettingConnection ? '重置中...' : '重置Gun连接' }}
          </ion-button>
        </div>
      </div>
    </ion-content>
  </ion-modal>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  IonModal, IonHeader, IonToolbar, IonTitle, IonButtons, IonButton, IonIcon,
  IonContent, toastController
} from '@ionic/vue';
import {
  closeOutline, checkmarkCircleOutline, alertCircleOutline, hourglassOutline
} from 'ionicons/icons';
import { getTalkFlowCore } from '@/composables/TalkFlowCore';
import { useNetworkStatus } from '@/composables/useNetworkStatus';
import { v4 as uuidv4 } from 'uuid';

interface Props {
  isOpen: boolean;
}

const props = defineProps<Props>();
const emit = defineEmits<{
  close: [];
}>();

const { t } = useI18n();
const { gun, enabledPeers, reinitializeGunInstance } = getTalkFlowCore();

// 诊断状态
const networkStatus = ref<'online' | 'offline'>('offline');
const gunConnectionStatus = ref<'connected' | 'disconnected'>('disconnected');
const commentTestStatus = ref<'untested' | 'testing' | 'success' | 'failed'>('untested');
const queueSize = ref(0);
const lastCheckTime = ref('');
const isRunningDiagnostic = ref(false);
const isResettingConnection = ref(false);

// 修复建议
const fixSuggestions = ref<Array<{
  id: string;
  description: string;
  actionText?: string;
  action?: () => void;
  isExecuting?: boolean;
}>>([]);

const enabledPeersCount = computed(() => enabledPeers.value.length);

const showFixSuggestions = computed(() => {
  return networkStatus.value === 'offline' || 
         gunConnectionStatus.value === 'disconnected' || 
         commentTestStatus.value === 'failed';
});

const getTestStatusText = (status: string) => {
  switch (status) {
    case 'testing': return '测试中...';
    case 'success': return '正常';
    case 'failed': return '失败';
    default: return '未测试';
  }
};

// 检查网络状态
const checkNetworkStatus = () => {
  networkStatus.value = navigator.onLine ? 'online' : 'offline';
};

// 检查Gun连接状态
const checkGunConnection = async (): Promise<boolean> => {
  return new Promise((resolve) => {
    let resolved = false;
    const timeout = setTimeout(() => {
      if (!resolved) {
        resolved = true;
        gunConnectionStatus.value = 'disconnected';
        resolve(false);
      }
    }, 5000);

    gun.get('~health-check').once((data) => {
      if (!resolved) {
        resolved = true;
        clearTimeout(timeout);
        gunConnectionStatus.value = 'connected';
        resolve(true);
      }
    });

    // 尝试写入测试数据
    gun.get('health-check-' + Date.now()).put({ test: true }, (ack: any) => {
      if (!resolved && !ack.err) {
        resolved = true;
        clearTimeout(timeout);
        gunConnectionStatus.value = 'connected';
        resolve(true);
      }
    });
  });
};

// 测试评论保存功能
const testCommentSave = async () => {
  commentTestStatus.value = 'testing';
  
  try {
    const testMomentId = 'test-moment-' + Date.now();
    const testCommentId = uuidv4();
    const testComment = {
      commentId: testCommentId,
      momentId: testMomentId,
      userPub: 'test-user',
      content: 'Test comment for diagnostic',
      timestamp: Date.now(),
      replyToCommentId: null,
      parentCommentId: null,
      isDeleted: 0,
    };

    await new Promise<void>((resolve, reject) => {
      gun
        .get('moments')
        .get(testMomentId)
        .get('comments')
        .get(testCommentId)
        .put(testComment, (ack: any) => {
          if (ack.err) {
            reject(new Error(ack.err));
          } else {
            resolve();
          }
        });
    });

    // 尝试读取保存的评论
    await new Promise<void>((resolve, reject) => {
      gun
        .get('moments')
        .get(testMomentId)
        .get('comments')
        .get(testCommentId)
        .once((data: any) => {
          if (data && data.content === testComment.content) {
            resolve();
          } else {
            reject(new Error('无法读取保存的评论'));
          }
        });
      
      setTimeout(() => reject(new Error('读取超时')), 3000);
    });

    commentTestStatus.value = 'success';
    
    // 清理测试数据
    gun.get('moments').get(testMomentId).put(null);
    
  } catch (error) {
    console.error('评论保存测试失败:', error);
    commentTestStatus.value = 'failed';
  }
};

// 检查队列大小
const checkQueueSize = () => {
  try {
    const root = gun._.root;
    if (root && root.opt && root.opt.outgoing) {
      queueSize.value = Object.keys(root.opt.outgoing).length;
    }
  } catch (error) {
    console.error('检查队列大小失败:', error);
  }
};

// 重置Gun连接
const resetGunConnection = async () => {
  isResettingConnection.value = true;
  
  try {
    await reinitializeGunInstance(enabledPeers.value);
    
    const toast = await toastController.create({
      message: 'Gun连接已重置',
      duration: 2000,
      position: 'top',
      color: 'success',
    });
    await toast.present();
    
    // 重新运行诊断
    await runFullDiagnostic();
    
  } catch (error) {
    console.error('重置Gun连接失败:', error);
    
    const toast = await toastController.create({
      message: '重置连接失败',
      duration: 2000,
      position: 'top',
      color: 'danger',
    });
    await toast.present();
  } finally {
    isResettingConnection.value = false;
  }
};

// 生成修复建议
const generateFixSuggestions = () => {
  fixSuggestions.value = [];

  if (networkStatus.value === 'offline') {
    fixSuggestions.value.push({
      id: 'network',
      description: '网络连接异常，请检查网络设置',
    });
  }

  if (gunConnectionStatus.value === 'disconnected') {
    fixSuggestions.value.push({
      id: 'gun-connection',
      description: 'Gun.js节点连接失败，尝试重置连接',
      actionText: '重置连接',
      action: resetGunConnection,
    });
  }

  if (commentTestStatus.value === 'failed') {
    fixSuggestions.value.push({
      id: 'comment-save',
      description: '评论保存功能异常，建议重新测试或重置连接',
      actionText: '重新测试',
      action: testCommentSave,
    });
  }

  if (queueSize.value > 50) {
    fixSuggestions.value.push({
      id: 'queue-size',
      description: '请求队列过大，可能影响性能',
      actionText: '清理队列',
      action: async () => {
        try {
          const root = gun._.root;
          if (root && root.opt) {
            root.opt.outgoing = {};
          }
          checkQueueSize();
        } catch (error) {
          console.error('清理队列失败:', error);
        }
      },
    });
  }
};

// 运行完整诊断
const runFullDiagnostic = async () => {
  isRunningDiagnostic.value = true;
  
  try {
    // 检查网络状态
    checkNetworkStatus();
    
    // 检查Gun连接
    await checkGunConnection();
    
    // 测试评论保存
    await testCommentSave();
    
    // 检查队列大小
    checkQueueSize();
    
    // 生成修复建议
    generateFixSuggestions();
    
    lastCheckTime.value = new Date().toLocaleTimeString();
    
  } catch (error) {
    console.error('诊断过程出错:', error);
  } finally {
    isRunningDiagnostic.value = false;
  }
};

// 监听弹窗打开
watch(() => props.isOpen, (isOpen) => {
  if (isOpen) {
    runFullDiagnostic();
  }
});

onMounted(() => {
  if (props.isOpen) {
    runFullDiagnostic();
  }
});
</script>

<style scoped>
.diagnostic-modal {
  --height: 80vh;
  --border-radius: 12px;
}

.diagnostic-container {
  max-width: 100%;
}

.test-section, .fix-section, .details-section {
  margin-bottom: 24px;
  padding: 16px;
  background: var(--ion-color-light, #f8f9fa);
  border-radius: 8px;
}

.test-section h3, .fix-section h3, .details-section h3 {
  margin: 0 0 16px 0;
  font-size: 1.1em;
  font-weight: 600;
  color: var(--ion-color-primary);
}

.test-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid var(--ion-color-step-150, #e0e0e0);
}

.test-item:last-child {
  border-bottom: none;
}

.test-label {
  font-weight: 500;
}

.test-status {
  display: flex;
  align-items: center;
  gap: 6px;
  font-weight: 600;
}

.test-status.online, .test-status.connected, .test-status.success {
  color: var(--ion-color-success);
}

.test-status.offline, .test-status.disconnected, .test-status.failed {
  color: var(--ion-color-danger);
}

.test-status.testing {
  color: var(--ion-color-warning);
}

.fix-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid var(--ion-color-step-150, #e0e0e0);
}

.fix-item:last-child {
  border-bottom: none;
}

.fix-description {
  flex: 1;
  margin-right: 12px;
}

.info-item {
  padding: 8px 0;
  border-bottom: 1px solid var(--ion-color-step-150, #e0e0e0);
}

.info-item:last-child {
  border-bottom: none;
}

.action-buttons {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 24px;
}

/* 深色主题适配 */
@media (prefers-color-scheme: dark) {
  .test-section, .fix-section, .details-section {
    background: var(--ion-color-step-100, #2a2a2a);
  }
  
  .test-item, .fix-item, .info-item {
    border-bottom-color: var(--ion-color-step-300, #555);
  }
}
</style> 